const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface ApiClient {
  auth: {
    passkeyRegisterBegin: (input: { username: string }) => Promise<{
      challenge: string;
      tempToken: string;
      rp: { id: string; name: string };
      user: { id: string; name: string; displayName: string };
      pubKeyCredParams: Array<{ type: string; alg: number }>;
    }>;
    passkeyRegisterComplete: (input: {
      username: string;
      credential: {
        id: string;
        publicKeySpki: string;
        publicKeyAlgorithm: number;
        clientDataJSON: string;
        authenticatorData: string;
      };
      encryptedSk: string;
      nonce: string;
      argon2Salt: string;
      identityPk: string;
      signingPk: string;
    }) => Promise<{ userId: string; accessToken: string; refreshToken: string }>;
    passkeyLoginBegin: () => Promise<{
      challenge: string;
      rp: { id: string; name: string };
    }>;
    passkeyLoginComplete: (input: {
      credentialId: string;
      clientDataJSON: string;
      authenticatorData: string;
      signature: string;
    }) => Promise<{ userId: string; scratchUsername: string; accessToken: string; refreshToken: string }>;
    tokenRefresh: (input: { refreshToken: string }) => Promise<{ accessToken: string }>;
    logout: () => Promise<{ success: boolean }>;
    me: () => Promise<{ userId: string; scratchUsername: string; createdAt: string }>;
    getKeyEnvelope: () => Promise<{ encryptedSk: string; nonce: string; argon2Salt: string }>;
    updatePassphrase: (input: { newEncryptedSk: string; newNonce: string; newArgon2Salt: string }) => Promise<{ success: boolean }>;
  };
  circle: {
    createCircle: (input: {
      name: string;
      initialWrappedFeedKey: { version: string; ciphertext: string; ephemeralPk: string };
    }) => Promise<{ circleId: string }>;
    getMyCircles: () => Promise<{
      activeCircleId: string | null;
      joined: Array<{ circleId: string; name: string; role: 'owner' | 'member'; joinedAt: string }>;
      invites: Array<{ circleId: string; circleName: string; inviterUserId: string; inviterUsername: string; invitedAt: string }>;
    }>;
    setActiveCircle: (input: { circleId: string }) => Promise<{ success: boolean }>;
    inviteMember: (input: {
      circleId: string;
      inviteeUsername: string;
      wrappedFeedKeys: Array<{ version: string; ciphertext: string; ephemeralPk: string }>;
    }) => Promise<{ success: boolean }>;
    acceptInvite: (input: { circleId: string }) => Promise<{ success: boolean }>;
    declineInvite: (input: { circleId: string }) => Promise<{ success: boolean }>;
    getActiveCircle: () => Promise<{
      circleId: string;
      name: string;
      ownerUserId: string;
      createdAt: string;
      members: Array<{ userId: string; scratchUsername: string; role: 'owner' | 'member'; joinedAt: string }>;
    } | null>;
    leaveCircle: (input: { circleId: string }) => Promise<{ success: boolean }>;
    getActiveCircleId: () => Promise<{ circleId: string | null }>;
    requireActiveCircle: () => Promise<{ circleId: string; name: string }>;
  };
  user: {
    getIdentityPk: (input: { username: string }) => Promise<{ identityPk: string; fingerprint: string }>;
    getProfileByUsername: (input: { username: string }) => Promise<{ userId: string; scratchUsername: string; createdAt: string }>;
  };
  feed: {
    createPost: (input: { ciphertext: string; keyVersion: string; timestamp: string }) => Promise<{ postId: string }>;
    getPostsByUser: (input: { userId: string; before?: string; limit?: number }) => Promise<{
      posts: Array<{ postId: string; keyVersion: string; timestamp: string; ciphertext: string }>;
    }>;
    getTimeline: (input: { before?: string; limit?: number }) => Promise<{
      posts: Array<{ postId: string; authorId: string; keyVersion: string; timestamp: string; ciphertext: string }>;
    }>;
    deletePost: (input: { postId: string }) => Promise<{ success: boolean }>;
    getCircleMembers: () => Promise<{
      circleId: string;
      members: Array<{
        userId: string;
        scratchUsername: string;
        role: 'owner' | 'member';
        joinedAt: string;
      }>;
    }>;
    kickFromCircle: (input: {
      targetUserId: string;
      newKeyVersion: string;
      wrappedKeys: Array<{ userId: string; ciphertext: string; ephemeralPk: string }>;
    }) => Promise<{ success: boolean }>;
    getFeedKeys: (input: { userId: string }) => Promise<{
      encryptedKeys: Array<{ version: string; ciphertext: string; ephemeralPk?: string }>;
    }>;
  };
  dm: {
    createConversation: (input: {
      partnerUsername: string;
      fingerprintVerified: boolean;
      wrappedKeyForSelf: string;
      wrappedKeyForPartner: string;
    }) => Promise<{ dmId: string; partnerIdentityPk: string }>;
    getConversations: () => Promise<{
      circleId: string;
      conversations: Array<{ dmId: string; partnerUserId: string; partnerUsername: string; updatedAt: string }>;
    }>;
    deleteConversation: (input: { dmId: string }) => Promise<{ success: boolean }>;
    sendMessage: (input: { dmId: string; ciphertext: string; timestamp: string }) => Promise<{ messageId: string }>;
    getMessages: (input: { dmId: string; before?: string; limit?: number }) => Promise<{
      wrappedKey: string;
      messages: Array<{ messageId: string; senderId: string; timestamp: string; ciphertext: string }>;
    }>;
    deleteMessage: (input: { dmId: string; messageId: string }) => Promise<{ success: boolean }>;
  };
  group: {
    createGroup: (input: {
      name: string;
      members: Array<{ username: string; wrappedKey: string; ephemeralPk: string; fingerprintVerified: boolean }>;
      selfWrappedKey: { wrappedKey: string; ephemeralPk: string };
    }) => Promise<{ groupId: string }>;
    getGroups: () => Promise<{ circleId: string; groups: Array<{ groupId: string; name: string; role: string; updatedAt: string }> }>;
    getGroup: (input: { groupId: string }) => Promise<{
      meta: {
        groupId: string;
        circleId: string;
        name: string;
        createdAt: string;
        memberLimit: number;
        rotationState: 'idle' | 'in_progress' | 'committed';
        members: Array<{ userId: string; username: string; role: 'owner' | 'admin' | 'moderator' | 'member'; joinedAt: string; fingerprintVerified: boolean }>;
      };
      wrappedKey: string;
      ephemeralPk: string;
    }>;
    deleteGroup: (input: { groupId: string }) => Promise<{ success: boolean }>;
    transferOwner: (input: { groupId: string; newOwnerUserId: string }) => Promise<{ success: boolean }>;
    addMember: (input: { groupId: string; username: string; wrappedKey: string; ephemeralPk: string; fingerprintVerified: boolean }) => Promise<{ success: boolean }>;
    removeMember: (input: { groupId: string; targetUserId: string; newWrappedKeys: Array<{ userId: string; wrappedKey: string; ephemeralPk: string }> }) => Promise<{ success: boolean }>;
    updateMemberRole: (input: { groupId: string; targetUserId: string; role: 'admin' | 'moderator' | 'member' }) => Promise<{ success: boolean }>;
    sendMessage: (input: { groupId: string; ciphertext: string; timestamp: string }) => Promise<{ messageId: string }>;
    getMessages: (input: { groupId: string; before?: string; limit?: number }) => Promise<{
      messages: Array<{ messageId: string; senderId: string; timestamp: string; ciphertext: string }>;
    }>;
    deleteMessage: (input: { groupId: string; messageId: string }) => Promise<{ success: boolean }>;
  };
}

async function request<T>(
  path: string,
  body: unknown,
  options?: { token?: string; methodOverride?: 'GET' | 'POST'; retryOnAuthError?: boolean }
): Promise<T> {
  const hasLocalSession = (): boolean => {
    if (typeof window === 'undefined') {
      return false;
    }
    return Boolean(localStorage.getItem('accessToken') && localStorage.getItem('userId'));
  };

  const clearLocalSession = (): void => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('identitySkRaw');
    localStorage.removeItem('atten:accounts');
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (options?.token) {
    headers['Authorization'] = `Bearer ${options.token}`;
  }

  if (options?.methodOverride) {
    headers['x-http-method-override'] = options.methodOverride;
  }
  
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  if (response.status === 401 && path !== '/trpc/auth.tokenRefresh') {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken && options?.retryOnAuthError !== false) {
      const refreshResponse = await fetch(`${API_URL}/trpc/auth.tokenRefresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (refreshResponse.ok) {
        const refreshed = await refreshResponse.json() as { result?: { data?: { accessToken?: string } } };
        const accessToken = refreshed.result?.data?.accessToken;
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          return request(path, body, {
            ...(options?.methodOverride ? { methodOverride: options.methodOverride } : {}),
            token: accessToken,
            retryOnAuthError: false
          });
        }
      }
    }
    
    // If we reach here, it's a 401 and refresh failed or wasn't possible
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && currentPath !== '/login' && currentPath !== '/register') {
        clearLocalSession();
        window.location.href = '/';
      }
    }
  }

  if (response.status === 500 && hasLocalSession() && path !== '/trpc/auth.passkeyRegisterBegin') {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (currentPath !== '/' && currentPath !== '/login' && currentPath !== '/register') {
      clearLocalSession();
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' })) as { message?: string };
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  
  const json = await response.json() as { result?: { data?: T }; error?: { message?: string } };
  
  if (json.error) {
    throw new Error(json.error.message || 'Unknown error');
  }
  
  return json.result?.data as T;
}

function getToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('accessToken') ?? undefined;
}

function authOptions(methodOverride?: 'GET' | 'POST'): { token?: string; methodOverride?: 'GET' | 'POST' } {
  const token = getToken();
  return {
    ...(token ? { token } : {}),
    ...(methodOverride ? { methodOverride } : {})
  };
}

export const api: ApiClient = {
  auth: {
    passkeyRegisterBegin: (input: { username: string }) => request('/trpc/auth.passkeyRegisterBegin', input),
    passkeyRegisterComplete: (input) => request('/trpc/auth.passkeyRegisterComplete', input),
    passkeyLoginBegin: () => request('/trpc/auth.passkeyLoginBegin', {}),
    passkeyLoginComplete: (input) => request('/trpc/auth.passkeyLoginComplete', input),
    tokenRefresh: (input) => request('/trpc/auth.tokenRefresh', input),
    logout: () => request('/trpc/auth.logout', {}, authOptions()),
    me: () => request('/trpc/auth.me', {}, authOptions('GET')),
    getKeyEnvelope: () => request('/trpc/auth.getKeyEnvelope', {}, authOptions('GET')),
    updatePassphrase: (input) => request('/trpc/auth.updatePassphrase', input, authOptions())
  },
  circle: {
    createCircle: (input) => request('/trpc/circle.createCircle', input, authOptions()),
    getMyCircles: () => request('/trpc/circle.getMyCircles', {}, authOptions('GET')),
    setActiveCircle: (input) => request('/trpc/circle.setActiveCircle', input, authOptions()),
    inviteMember: (input) => request('/trpc/circle.inviteMember', input, authOptions()),
    acceptInvite: (input) => request('/trpc/circle.acceptInvite', input, authOptions()),
    declineInvite: (input) => request('/trpc/circle.declineInvite', input, authOptions()),
    getActiveCircle: () => request('/trpc/circle.getActiveCircle', {}, authOptions('GET')),
    leaveCircle: (input) => request('/trpc/circle.leaveCircle', input, authOptions()),
    getActiveCircleId: () => request('/trpc/circle.getActiveCircleId', {}, authOptions('GET')),
    requireActiveCircle: () => request('/trpc/circle.requireActiveCircle', {}, authOptions('GET'))
  },
  user: {
    getIdentityPk: (input) => request('/trpc/user.getIdentityPk', input, authOptions('GET')),
    getProfileByUsername: (input) => request('/trpc/user.getProfileByUsername', input, authOptions('GET'))
  },
  feed: {
    createPost: (input) => request('/trpc/feed.createPost', input, authOptions()),
    getPostsByUser: (input) => request('/trpc/feed.getPostsByUser', input, authOptions('GET')),
    getTimeline: (input) => request('/trpc/feed.getTimeline', input, authOptions('GET')),
    deletePost: (input) => request('/trpc/feed.deletePost', input, authOptions()),
    getCircleMembers: () => request('/trpc/feed.getCircleMembers', {}, authOptions('GET')),
    kickFromCircle: (input) => request('/trpc/feed.kickFromCircle', input, authOptions()),
    getFeedKeys: (input) => request('/trpc/feed.getFeedKeys', input, authOptions('GET'))
  },
  dm: {
    createConversation: (input) => request('/trpc/dm.createConversation', input, authOptions()),
    getConversations: () => request('/trpc/dm.getConversations', {}, authOptions('GET')),
    deleteConversation: (input) => request('/trpc/dm.deleteConversation', input, authOptions()),
    sendMessage: (input) => request('/trpc/dm.sendMessage', input, authOptions()),
    getMessages: (input) => request('/trpc/dm.getMessages', input, authOptions('GET')),
    deleteMessage: (input) => request('/trpc/dm.deleteMessage', input, authOptions())
  },
  group: {
    createGroup: (input) => request('/trpc/group.createGroup', input, authOptions()),
    getGroups: () => request('/trpc/group.getGroups', {}, authOptions('GET')),
    getGroup: (input) => request('/trpc/group.getGroup', input, authOptions('GET')),
    deleteGroup: (input) => request('/trpc/group.deleteGroup', input, authOptions()),
    transferOwner: (input) => request('/trpc/group.transferOwner', input, authOptions()),
    addMember: (input) => request('/trpc/group.addMember', input, authOptions()),
    removeMember: (input) => request('/trpc/group.removeMember', input, authOptions()),
    updateMemberRole: (input) => request('/trpc/group.updateMemberRole', input, authOptions()),
    sendMessage: (input) => request('/trpc/group.sendMessage', input, authOptions()),
    getMessages: (input) => request('/trpc/group.getMessages', input, authOptions('GET')),
    deleteMessage: (input) => request('/trpc/group.deleteMessage', input, authOptions())
  }
};
